import { useState, useEffect, useCallback } from 'react'
import type { Product, CartItem } from '../types'

const CART_STORAGE_KEY = 'eco-shift-cart'

function loadCartFromStorage(): CartItem[] {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

function saveCartToStorage(items: CartItem[]): void {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items))
  } catch {
    console.error('Failed to save cart to localStorage')
  }
}

export function useCart() {
  const [items, setItems] = useState<CartItem[]>(loadCartFromStorage)

  useEffect(() => {
    saveCartToStorage(items)
  }, [items])

  const addItem = useCallback((product: Product) => {
    setItems((prev) => {
      const existingIndex = prev.findIndex((item) => item.product.id === product.id)

      if (existingIndex >= 0) {
        const updated = [...prev]
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + 1,
        }
        return updated
      }

      return [...prev, { product, quantity: 1 }]
    })
  }, [])

  const removeItem = useCallback((productId: string) => {
    setItems((prev) => prev.filter((item) => item.product.id !== productId))
  }, [])

  const updateQuantity = useCallback((productId: string, quantity: number) => {
    if (quantity <= 0) {
      setItems((prev) => prev.filter((item) => item.product.id !== productId))
      return
    }

    setItems((prev) =>
      prev.map((item) => (item.product.id === productId ? { ...item, quantity } : item))
    )
  }, [])

  const clearCart = useCallback(() => {
    setItems([])
  }, [])

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0)

  const subtotal = items.reduce((sum, item) => sum + item.product.price * item.quantity, 0)

  // Logic for 15% bundle discount: 1 frame + 4 covers
  const hasFrame = items.some((item) => item.product.type === 'frame')
  const coverCount = items
    .filter((item) => item.product.id.startsWith('cover-'))
    .reduce((sum, item) => sum + item.quantity, 0)

  const isBundleEligible = hasFrame && coverCount >= 4
  const discount = isBundleEligible ? subtotal * 0.15 : 0
  const total = subtotal - discount

  return {
    items,
    itemCount,
    subtotal,
    discount,
    total,
    isBundleEligible,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
  }
}
